import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { ChakraProvider } from "@chakra-ui/react";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import Label from "./pages/Label";
import Home from "./pages/Home";
import Release from "./pages/Release";
import Report from "./pages/Report";
import ResetPassword from "./pages/ResetPassword";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Layout from "./components/Layout";
import AlbumDetail from "./pages/AlbumDetail";
import CollaborationLayout from "./components/layouts/CollaborationLayout";
import CollaborationHome from "./pages/MusicCollaboration/Home";
import ChannelManagement from "./pages/MusicCollaboration/ChannelManagement";
import AlbumList from "./pages/MusicCollaboration/AlbumList";
import Dashboard from "./pages/MusicCollaboration/Dashboard";
import RevenueByVideo from "./pages/MusicCollaboration/RevenueByVideo";
import RevenueByChannel from "./pages/MusicCollaboration/RevenueByChannel";
import SettlementManagement from "./pages/MusicCollaboration/SettlementManagement";
import TermsOfService from "./pages/policy/TermsOfService";
import PrivacyPolicy from "./pages/policy/PrivacyPolicy";
import DigitalContentTerms from "./pages/policy/DigitalContentTerms";
import MyPage from "./pages/MyPage";
import Partners from "./pages/MusicCollaboration/Partners";
import LicensedVideoRequest from "./pages/MusicCollaboration/LicensedVideoRequest";
import AdminLayout from "./components/admin/AdminLayout";
import Users from "./pages/admin/members/Users";
import Contracts from "./pages/admin/shorts/Contracts";
import CompletedUserSettlement from "./pages/admin/settlement/CompletedUser";
import PendingUserSettlement from "./pages/admin/settlement/PendingUser";
import FeeManagement from "./pages/admin/settlement/FeeManagement";
import LicenseVideos from "./pages/admin/shorts/LicenseVideos";
import Channels from "./pages/admin/shorts/Channels";
import AlbumsList from "./pages/admin/albums/AlbumsList";
import Ownership from "./pages/admin/albums/Ownership";
import Tracks from "./pages/admin/albums/tracks";
import Labels from "./pages/admin/albums/labels";
import Musicians from "./pages/admin/albums/musicians";
import ShortsAlbums from "./pages/admin/shorts/albums";
import List from "./pages/admin/shorts/List";
import Youtube from "./pages/admin/settlement/youtube";

// 보호된 라우트 컴포넌트
const ProtectedRoute = ({ children }) => {
  const { user } = useAuth();
  if (!user) {
    return <Navigate to="/login" />;
  }
  return <Layout>{children}</Layout>;
};

// 공개 라우트 컴포넌트
const PublicRoute = ({ children }) => {
  return <Layout>{children}</Layout>;
};

// 협업 라우트 컴포넌트
const CollaborationRoute = ({ children }) => {
  const { user } = useAuth();
  if (!user) {
    return <Navigate to="/login" />;
  }
  return <CollaborationLayout>{children}</CollaborationLayout>;
};

// 어드민 라우트 컴포넌트
const AdminRoute = ({ children }) => {
  const { user } = useAuth();
  if (!user) {
    return <Navigate to="/login" />;
  }
  return <AdminLayout>{children}</AdminLayout>;
};

function App() {
  return (
    <ChakraProvider>
      <AuthProvider>
        <Router>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route
              path="/signup"
              element={
                <PublicRoute>
                  <Signup />
                </PublicRoute>
              }
            />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route
              path="/label"
              element={
                <ProtectedRoute>
                  <Label />
                </ProtectedRoute>
              }
            />
            <Route
              path="/release"
              element={
                <ProtectedRoute>
                  <Release />
                </ProtectedRoute>
              }
            />
            <Route
              path="/report"
              element={
                <ProtectedRoute>
                  <Report />
                </ProtectedRoute>
              }
            />
            <Route
              path="/"
              element={
                <PublicRoute>
                  <Home />
                </PublicRoute>
              }
            />
            <Route
              path="/album/:id"
              element={
                <ProtectedRoute>
                  <AlbumDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/laeeblyforshorts"
              element={
                <CollaborationRoute>
                  <CollaborationHome />
                </CollaborationRoute>
              }
            />
            <Route
              path="/collaboration/dashboard"
              element={
                <CollaborationRoute>
                  <Dashboard />
                </CollaborationRoute>
              }
            />
            <Route
              path="/collaboration/channels"
              element={
                <CollaborationRoute>
                  <ChannelManagement />
                </CollaborationRoute>
              }
            />
            <Route
              path="/collaboration/albums"
              element={
                <CollaborationRoute>
                  <AlbumList />
                </CollaborationRoute>
              }
            />
            <Route
              path="/collaboration/revenue-by-video"
              element={
                <CollaborationRoute>
                  <RevenueByVideo />
                </CollaborationRoute>
              }
            />
            <Route
              path="/collaboration/revenue-by-channel"
              element={
                <CollaborationRoute>
                  <RevenueByChannel />
                </CollaborationRoute>
              }
            />
            <Route
              path="/collaboration/settlements"
              element={
                <CollaborationRoute>
                  <SettlementManagement />
                </CollaborationRoute>
              }
            />
            <Route
              path="/collaboration/partners"
              element={
                <CollaborationRoute>
                  <Partners />
                </CollaborationRoute>
              }
            />
            <Route
              path="/collaboration/licensed-video-request"
              element={
                <CollaborationRoute>
                  <LicensedVideoRequest />
                </CollaborationRoute>
              }
            />
            <Route
              path="/terms"
              element={
                <PublicRoute>
                  <TermsOfService />
                </PublicRoute>
              }
            />
            <Route
              path="/privacy"
              element={
                <PublicRoute>
                  <PrivacyPolicy />
                </PublicRoute>
              }
            />
            <Route
              path="/digital-content-terms"
              element={
                <PublicRoute>
                  <DigitalContentTerms />
                </PublicRoute>
              }
            />
            <Route
              path="/mypage"
              element={
                <ProtectedRoute>
                  <MyPage />
                </ProtectedRoute>
              }
            />
            {/* 어드민 라우트 */}
            <Route
              path="/admin/*"
              element={
                <AdminRoute>
                  <Routes>
                    <Route
                      path="/"
                      element={<Navigate to="/admin/dashboard" replace />}
                    />
                    <Route path="/dashboard" element={<div>대시보드</div>} />
                    <Route path="/members/users" element={<Users />} />
                    <Route
                      path="/members/membership"
                      element={<div>멤버쉽 회원</div>}
                    />
                    <Route path="/albums/list" element={<AlbumsList />} />
                    <Route path="/shorts/ownership" element={<Ownership />} />
                    <Route path="/albums/tracks" element={<Tracks />} />
                    <Route path="/albums/musicians" element={<Musicians />} />
                    <Route path="/albums/labels" element={<Labels />} />
                    <Route path="/shorts/contracts" element={<Contracts />} />
                    <Route path="/shorts/channels" element={<Channels />} />
                    <Route path="/shorts/albums" element={<ShortsAlbums />} />
                    <Route
                      path="/shorts/channels"
                      element={<div>쇼츠 채널</div>}
                    />
                    <Route path="/shorts/list" element={<List />} />
                    <Route path="/shorts/license" element={<LicenseVideos />} />
                    <Route
                      path="/settlement/commission"
                      element={<FeeManagement />}
                    />
                    <Route
                      path="/settlement/pending-users"
                      element={<PendingUserSettlement />}
                    />
                    <Route
                      path="/settlement/completed-users"
                      element={<CompletedUserSettlement />}
                    />
                    <Route
                      path="/settlement/pending-partners"
                      element={<div>예정 파트너</div>}
                    />
                    <Route
                      path="/settlement/completed-partners"
                      element={<div>완료 파트너</div>}
                    />
                    <Route path="/settlement/youtube" element={<Youtube />} />
                    <Route
                      path="/support/notices"
                      element={<div>공지사항</div>}
                    />
                    <Route path="/support/faq" element={<div>FAQ</div>} />
                    <Route
                      path="/support/inquiries"
                      element={<div>사용자 문의</div>}
                    />
                    <Route path="/settings" element={<div>환경설정</div>} />
                    <Route path="/albums/ownership" element={<Ownership />} />
                  </Routes>
                </AdminRoute>
              }
            />
          </Routes>
        </Router>
      </AuthProvider>
    </ChakraProvider>
  );
}

export default App;
